/**
 * customer-actions — the shared 360-drawer action modals, ported from the
 * source prototype's customer-actions.jsx (CACompose / CABook / CARegister /
 * CAItem). Structure, copy and layout are the source's, transcribed verbatim;
 * translated to jrni-ui + inline styles. Every confirm AWAITS its typed op
 * (customers-api) and toasts only on resolve — the source fired the toast
 * without persisting; per this project's no-fake-interactivity rule the port
 * wires the op (preview-seeded, REQ-065). Themed via `theme` = { accent,
 * accentSoft, ink }.
 *
 * DS MIGRATION (2026-08-10) — the four modals now COMPOSE jrni-ui instead of
 * re-forking it. What was hand-rolled here, and what replaced it:
 *   `overlay()` + CAShell chrome  → `Modal` (focus trap, body scroll-lock,
 *                                   role=dialog/aria-modal, Escape, the
 *                                   surface-overlay backdrop token, T.z.modal)
 *   the `btn` style const          → `Btn` variant secondary / primary / danger
 *   CATabs                         → `SegmentedControl`
 *   CAField                        → `Field` + `SectionLabel as="span"`
 *   `chipStyle` service chips      → `ChipToggleGroup`
 *   `chipStyle` day chips          → `DayStrip`
 *   `chipStyle` slot chips         → `SlotPicker`
 *   the event option rows          → `SelectableTile`
 *   the k/v detail rows            → `SummaryList` / `SummaryRow`
 *   the status chip                → `Pill`
 *   the note callout               → `Banner size="compact"`
 *   loading / empty / error        → `Spinner` + `InlineEmptyState` / `Banner`
 *
 * The tenant `theme.accent` stays raw served DATA (a per-tenant brand hex, the
 * documented exception in customers-config.ts — NOT a token) and is now applied
 * in exactly two places: the modal header band and the primary CTA fill. Every
 * SELECTION surface reads the brand TOKEN instead, which is both the DS answer
 * and the only way those states resolve in dark mode.
 *
 * ACCENT WIRING (2026-08-11) — that last paragraph described the intent, but the
 * two halves did not meet: the band painted the SERVED accent while the brand
 * TOKEN was still the DS default, so a tenant on any accent but the default got
 * an accent band above violet chips, days, slots and tiles. `TenantBrandScope`
 * (lib/tenantBrand) closes it by declaring the tenant's accent onto the brand
 * custom properties at the modal root — the same properties tokens.css's
 * `[data-tenant]` blocks set, which the app had never set. Nothing about the
 * selection surfaces changed: they still read the token, the token now carries
 * the tenant. The scope sits INSIDE this file rather than on the screen root on
 * purpose — these modals are also reachable from the portalled 360 drawer, and
 * a portal escapes any scope higher up the React tree.
 */
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import {
  Icon, T, alpha, Btn, Input, Textarea, Modal, Field, SectionLabel, SegmentedControl,
  ChipToggleGroup, DayStrip, SlotPicker, SelectableTile, SummaryList, SummaryRow,
  InlineEmptyState, Banner, Spinner, Pill,
} from "@v0y4ge/jrni-ui";
import { toast } from "../../shell/toast";
import { TenantBrandScope } from "../../lib/tenantBrand";
import { messageCustomer, registerCustomerForEvent, customerItemAction } from "./customers-api";
import { bookAppointment, listApptSlots, type ApptSlot } from "../appointments/appointments-api";
import type { EvtOption } from "./customers-config";

import { useT } from "@v0y4ge/i18n-runtime";
export interface CAtheme { accent: string; accentSoft: string; ink: string; }
export interface CAcustomer { id: string; name: string; email: string; phone: string; brand: string; prefChannel: string; prefTime: string; }

/** The ONE field-stack rhythm inside every CA modal body. Replaces the deleted
 *  CAField's per-field off-scale bottom margin (14px) with one tokenized gap. */
const stack: CSSProperties = { display: "flex", flexDirection: "column", gap: T.space[4] };

/** Field label treatment shared by all four modals — the uppercase overline. */
const overline = (text: ReactNode) => <SectionLabel as="span">{text}</SectionLabel>;

function CAShell({ theme, icon, title, sub, onClose, children, footer, wide }: { theme: CAtheme; icon: ReactNode; title: string; sub?: string | null; onClose: () => void; children: ReactNode; footer: ReactNode; wide?: boolean }) {
  const t = useT();
  return (
    <TenantBrandScope brand={theme}>
    <Modal open onClose={onClose} showCloseButton={false} maxWidth={wide ? 560 : 460} aria-label={title}>
      {/* The ONE bespoke surface left in this file: a tenant-brand header band.
          Modal's own header is a white/neutral-1 treatment and cannot express a
          colored band, so it is suppressed (`showCloseButton={false}`, no
          `title`) and this renders in its place — but every value inside it is
          token-resolved, and the horizontal gutter now matches Modal.Body's 24. */}
      <div style={{ background: theme.accent, color: T.onColor, display: "flex", alignItems: "center", gap: T.space[3], padding: `${T.space[4]} ${T.space[6]}`, flexShrink: 0 }}>
        <span style={{ display: "inline-flex", opacity: 0.92 }}>{icon}</span>
        <div style={{ display: "flex", flexDirection: "column", gap: T.space[1], flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: T.text.h3.fontSize, fontWeight: 700 }}>{title}</span>
          {sub ? <span style={{ fontSize: T.text.label.fontSize, color: alpha(T.onColor, 75) }}>{sub}</span> : null}
        </div>
        {/* Was a raw <button> carrying a `// ui-raw` note that IconButton's fixed
            chrome (bordered, sm-radius, n1-on-white) can't do a borderless
            white-on-color circle. True of IconButton — but `Btn variant="primary"`
            already ships onColor ink and takes an inline background, so the
            circle is two overrides on a DS primitive instead of a fork, and it
            picks up the button state layer (hover/press/focus-visible) for free. */}
        <Btn
          variant="primary" size="sm" iconOnly={<Icon.x size={16} />}
          aria-label={t('customers.actions.ariaLabel')} onClick={onClose}
          style={{ background: alpha(T.onColor, 12), borderRadius: T.radius.pill, flexShrink: 0 }}
        />
      </div>
      <Modal.Body>{children}</Modal.Body>
      <Modal.Footer>{footer}</Modal.Footer>
    </Modal>
    </TenantBrandScope>
  );
}

/* ── Compose email / SMS ─────────────────────────────────────────────── */
export function CACompose({ theme, customer, onClose, defaultChannel }: { theme: CAtheme; customer: CAcustomer; onClose: () => void; defaultChannel?: "email" | "sms" }) {
  const t = useT();
  const hasEmail = !!customer.email, hasPhone = !!customer.phone;
  const [ch, setCh] = useState<"email" | "sms">(defaultChannel || (hasEmail ? "email" : "sms"));
  const first = (customer.name || "").split(" ")[0];
  const [subject, setSubject] = useState("A note from " + (customer.brand || "us"));
  const [body, setBody] = useState(`Dear ${first},\n\n`);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await messageCustomer(customer.id, { channel: ch, to: ch === "email" ? customer.email : customer.phone, subject: ch === "email" ? subject : undefined, body });
      toast({ tone: "success", title: ch === "email" ? "Email sent" : "SMS sent", subtitle: `${customer.name} · ${ch === "email" ? customer.email : customer.phone}` });
      onClose();
    } catch (e) { toast({ tone: "danger", title: "Couldn't send", subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  const smsLen = body.length;
  const channels: { value: "email" | "sms"; label: string; icon: ReactNode }[] = [
    ...(hasEmail ? [{ value: "email" as const, label: "Email", icon: <Icon.mail size={13} /> }] : []),
    ...(hasPhone ? [{ value: "sms" as const, label: "SMS", icon: <Icon.phone size={13} /> }] : []),
  ];
  return (
    <CAShell theme={theme} icon={<Icon.mail size={17} />} title={/* TODO(i18n): replace template-literal with t('customers.actions.title14', { ... }) */`Message ${customer.name}`} sub={customer.prefChannel ? `Prefers ${customer.prefChannel}` : null} onClose={onClose}
      footer={<>
        <Btn variant="secondary" onClick={onClose}>{t('customers.actions.text')}</Btn>
        <Btn variant="primary" icon={<Icon.mail size={14} />} disabled={busy} onClick={() => void send()} style={{ background: theme.accent }}>{busy ? t('customers.actions.text2') : /* TODO(i18n): replace template-literal with t('customers.actions.text3', { ... }) */`Send ${ch === "email" ? "email" : "SMS"}`}</Btn>
      </>}>
      <div style={stack}>
        {/* Only render the switcher when there is something to switch TO. The
            deleted CATabs rendered a lone tab for a customer with just an email;
            as a real radiogroup that is a one-option radiogroup, which announces
            a choice that does not exist. */}
        {channels.length > 1 ? (
          <SegmentedControl
            full options={channels} value={ch} onChange={setCh}
            ariaLabel={t('customers.actions.channelGroup', 'Message channel')}
          />
        ) : null}
        <Field label={overline(t('customers.actions.label12'))}><Input readOnly value={ch === "email" ? customer.email : customer.phone} /></Field>
        {ch === "email" ? <Field label={overline(t('customers.actions.label13'))}><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field> : null}
        <Field label={overline(t('customers.actions.label14'))} hint={ch === "sms" ? `${smsLen} characters · ${Math.max(1, Math.ceil(smsLen / 160))} segment(s)` : "Sends from the shared inbox · reply-to tracked on the record."}>
          <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
      </div>
    </CAShell>
  );
}

/* ── Book an appointment on the customer's behalf ──────────────────────
   CONVERGED 2026-07-26 (APT-02b): availability + booking now go through the
   CANONICAL appointments family (listApptSlots / bookAppointment, REQ-089) —
   the hardcoded times array is gone and the customer-namespace
   bookCustomerAppointment (REQ-069) is superseded. CAShell/tenant theming
   kept verbatim; slots name their staffer like every other booking surface. */
const slotKey = (s: ApptSlot) => `${s.start}|${s.staffId}`;

export function CABook({ theme, customer, services, onClose, presetService }: { theme: CAtheme; customer: CAcustomer; services: string[]; onClose: () => void; presetService?: string | null }) {
  const t = useT();
  const days = useMemo(() => { const out: Date[] = []; const base = new Date(); for (let i = 1; i <= 6; i++) { const d = new Date(base); d.setDate(base.getDate() + i); out.push(d); } return out; }, []);
  const [svc, setSvc] = useState(presetService || services[0] || "");
  const [day, setDay] = useState(days[0].toISOString().slice(0, 10));
  const [slots, setSlots] = useState<ApptSlot[] | null>(null);
  const [slotErr, setSlotErr] = useState<string | null>(null);
  const [pick, setPick] = useState<ApptSlot | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!svc) return;
    let on = true;
    setSlots(null); setSlotErr(null); setPick(null);
    listApptSlots({ service: svc, date: day }).then((r) => { if (on) setSlots(r.slots); }).catch((e) => { if (on) setSlotErr(e instanceof Error ? e.message : String(e)); });
    return () => { on = false; };
  }, [svc, day]);
  const fmtDay = (d: Date) => d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  // DayStrip stacks a bold label over a muted sublabel, so the one-line
  // "Mon, 11 Aug" chip splits into its two natural halves. Same Intl source.
  const fmtWeekday = (d: Date) => d.toLocaleDateString("en-GB", { weekday: "short" });
  const fmtDayMonth = (d: Date) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const ready = !!(svc && day && pick);
  const confirm = async () => {
    if (!ready || busy || !pick) return;
    setBusy(true);
    try {
      const a = await bookAppointment({ service: svc, date: day, start: pick.start, end: pick.end, staffId: pick.staffId, staffName: pick.staffName, customer: { name: customer.name, email: customer.email || undefined, phone: customer.phone || undefined }, origin: "customers" });
      toast({ tone: "success", title: "Appointment booked", subtitle: `${a.ref} · ${svc} · ${customer.name} · ${fmtDay(new Date(day + "T00:00:00"))} at ${pick.start}` });
      onClose();
    } catch (e) { toast({ tone: "danger", title: "Booking failed", subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <CAShell theme={theme} icon={<Icon.calendar size={17} />} title={/* TODO(i18n): replace template-literal with t('customers.actions.title15', { ... }) */`Book for ${customer.name}`} sub={t('customers.actions.sub')} onClose={onClose} wide
      footer={<>
        <Btn variant="secondary" onClick={onClose}>{t('customers.actions.text')}</Btn>
        <Btn variant="primary" icon={<Icon.check size={14} />} disabled={!ready || busy} onClick={() => void confirm()} style={{ background: theme.accent }}>{busy ? t('customers.actions.text4') : t('customers.actions.text5')}</Btn>
      </>}>
      <div style={stack}>
        <Field label={overline(t('customers.actions.label15'))}>
          <ChipToggleGroup
            ariaLabel={t('customers.actions.label15')} value={svc}
            options={services.map((s) => ({ value: s, label: s }))}
            onChange={(v) => setSvc(v as string)}
          />
        </Field>
        <Field label={overline(t('customers.actions.label16'))}>
          <DayStrip
            value={day} onChange={setDay}
            days={days.map((d) => { const id = d.toISOString().slice(0, 10); return { value: id, label: fmtWeekday(d), sublabel: fmtDayMonth(d) }; })}
          />
        </Field>
        {/* The four slot states share ONE box geometry (InlineEmptyState size="sm"
            for loading + empty, a compact danger Banner for error) so the
            surface never changes shape as availability resolves. */}
        <Field label={overline(t('customers.actions.label17'))} hint={customer.prefTime ? `Customer usually prefers: ${customer.prefTime}` : undefined}>
          {slotErr ? (
            <Banner tone="danger" size="compact" title={slotErr} />
          ) : !slots ? (
            <InlineEmptyState size="sm" icon={<Spinner size="md" />} title={t('appointments.sheet.loadingSlots', 'Loading availability…')} />
          ) : slots.length === 0 ? (
            <InlineEmptyState size="sm" title={t('appointments.sheet.noSlots', 'No open times for this day — try another day.')} />
          ) : (
            /* `labelFigures` + the per-slot `title` restore two things the DS
               migration dropped. The sublabel is still the FIRST name (a ~96px
               column can't hold "Marguerite Vandenberghe"), but the full name is
               reachable again on hover/long-press instead of being discarded;
               and the times run on a fixed-width digit grid so a column of them
               aligns, as the pre-migration mono chips did. */
            <SlotPicker
              ariaLabel={t('customers.actions.label17')}
              value={pick ? slotKey(pick) : null}
              labelFigures="mono"
              onChange={(v) => setPick(slots.find((s) => slotKey(s) === v) ?? null)}
              slots={slots.map((s) => ({ value: slotKey(s), label: s.start, sublabel: s.staffName.split(" ")[0], title: s.staffName }))}
            />
          )}
        </Field>
      </div>
    </CAShell>
  );
}

/* ── Register the customer for an event ──────────────────────────────── */
export function CARegister({ theme, customer, events, onClose }: { theme: CAtheme; customer: CAcustomer; events: EvtOption[]; onClose: () => void }) {
  const t = useT();
  const [pick, setPick] = useState<string | null>(events[0] ? events[0].id : null);
  const [party, setParty] = useState(1);
  const [busy, setBusy] = useState(false);
  const chosen = events.find((e) => e.id === pick);
  const confirm = async () => {
    if (!pick || !chosen || busy) return;
    setBusy(true);
    try {
      await registerCustomerForEvent(customer.id, { eventId: pick, eventTitle: chosen.title, party });
      toast({ tone: "success", title: "Registered for event", subtitle: `${customer.name} · ${chosen.title} · party of ${party}` });
      onClose();
    } catch (e) { toast({ tone: "danger", title: "Couldn't register", subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  return (
    <CAShell theme={theme} icon={<Icon.users size={17} />} title={/* TODO(i18n): replace template-literal with t('customers.actions.title16', { ... }) */`Register ${customer.name}`} sub={t('customers.actions.sub2')} onClose={onClose} wide
      footer={<>
        <Btn variant="secondary" onClick={onClose}>{t('customers.actions.text')}</Btn>
        <Btn variant="primary" icon={<Icon.check size={14} />} disabled={!pick || busy} onClick={() => void confirm()} style={{ background: theme.accent }}>{busy ? t('customers.actions.text6') : t('customers.actions.text7')}</Btn>
      </>}>
      <div style={stack}>
        <Field label={overline(t('customers.actions.label18'))}>
          {events.length ? (
            <div role="radiogroup" aria-label={t('customers.actions.label18')} style={{ display: "flex", flexDirection: "column", gap: T.space[2] }}>
              {events.map((e) => (
                <SelectableTile
                  key={e.id} selected={pick === e.id} onSelect={() => setPick(e.id)}
                  // A bare accent glyph, NOT an IconTile: the source row has no
                  // tinted tile and adding one would re-shape the row.
                  leading={<span style={{ color: theme.accent, display: "inline-flex" }}><Icon.calendar size={16} /></span>}
                  title={e.title}
                  description={`${e.date}${e.location ? " · " + e.location : ""}`}
                />
              ))}
            </div>
          ) : (
            <InlineEmptyState size="sm" title={t('customers.actions.text8')} />
          )}
        </Field>
        {events.length ? (
          <Field label={overline(t('customers.actions.label19'))}>
            <div style={{ display: "flex", gap: T.space[2], alignItems: "center" }}>
              {/* Icon.minus does not exist in the glyph set, so the ± affordances
                  keep their text glyphs — but as Btn `iconOnly` (square 36, real
                  focus ring) instead of the deleted `btn` fork, and they now
                  DISABLE at the clamp bounds rather than silently no-op. */}
              <Btn
                variant="secondary" iconOnly={<span aria-hidden="true">–</span>}
                aria-label={t('customers.actions.decreaseParty', 'Decrease party size')}
                disabled={party <= 1} onClick={() => setParty((p) => Math.max(1, p - 1))}
              />
              <span style={{ fontFamily: T.fontMono, fontSize: T.text.h3.fontSize, fontWeight: 700, minWidth: 28, textAlign: "center", color: T.n1 }}>{party}</span>
              <Btn
                variant="secondary" iconOnly={<Icon.plus size={14} />}
                aria-label={t('customers.actions.increaseParty', 'Increase party size')}
                disabled={party >= 6} onClick={() => setParty((p) => Math.min(6, p + 1))}
              />
              <span style={{ fontSize: T.text.label.fontSize, color: T.soft, marginLeft: T.space[1] }}>{t('customers.actions.text9')}{" "}{customer.name.split(" ")[0]}</span>
            </div>
          </Field>
        ) : null}
      </div>
    </CAShell>
  );
}

/* ── Open a history / upcoming item with contextual actions ──────────── */
export interface CAItemModel { kind: "appt" | "event" | "queue" | "wait"; waitState?: string; icon: ReactNode; title: string; sub?: string; upcoming?: boolean; status?: { label: string; fg: string; bg: string }; rows: { k: string; v: string }[]; note?: string; }

export function CAItem({ theme, customerId, item, onClose, onMessage, onRebook }: { theme: CAtheme; customerId: string; item: CAItemModel; onClose: () => void; onMessage?: () => void; onRebook?: (svc: string | null) => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const rows = item.rows || [];
  const isUpcoming = item.upcoming;
  const fire = async (action: string, ref: string | null, feedback: { title: string; sub?: string }) => {
    if (busy) return;
    setBusy(true);
    try {
      await customerItemAction(customerId, { kind: item.kind, action, ref });
      toast({ tone: "success", title: feedback.title, subtitle: feedback.sub });
    } catch (e) { toast({ tone: "danger", title: "Action failed", subtitle: e instanceof Error ? e.message : String(e) }); }
    finally { setBusy(false); }
  };
  interface Act { label: string; primary?: boolean; danger?: boolean; run: () => void; }
  const actions: Act[] = [];
  if (item.kind === "appt") {
    if (isUpcoming) { actions.push({ label: "Reschedule", run: () => void fire("reschedule", item.title, { title: "Reschedule link sent", sub: item.title }) }); actions.push({ label: "Cancel appointment", danger: true, run: () => void fire("cancel", item.title, { title: "Appointment cancelled", sub: item.title }) }); }
    else actions.push({ label: "Book again", primary: true, run: () => { onClose(); onRebook?.(item.title); } });
  } else if (item.kind === "event") {
    if (isUpcoming) { actions.push({ label: "View registration", run: () => void fire("view", item.title, { title: "Registration", sub: item.title }) }); actions.push({ label: "Cancel place", danger: true, run: () => void fire("release", item.title, { title: "Place released", sub: item.title }) }); }
    else actions.push({ label: "Similar events", run: () => void fire("similar", item.title, { title: "Finding similar events", sub: item.title }) });
  } else if (item.kind === "queue") {
    actions.push({ label: "Book a follow-up", primary: true, run: () => { onClose(); onRebook?.(null); } });
  } else if (item.kind === "wait") {
    if (item.waitState === "offered") { actions.push({ label: "Release hold", danger: true, run: () => void fire("release-hold", item.title, { title: "Hold released", sub: item.title }) }); actions.push({ label: "Book the slot", primary: true, run: () => { onClose(); onRebook?.(item.title); } }); }
    else if (item.waitState === "waiting") { actions.push({ label: "Remove from wait-list", danger: true, run: () => void fire("remove-wait", item.title, { title: "Removed from wait-list", sub: item.title }) }); actions.push({ label: "Offer a slot", primary: true, run: () => { onClose(); onRebook?.(item.title); } }); }
    else actions.push({ label: "Book again", primary: true, run: () => { onClose(); onRebook?.(item.title); } });
  }
  return (
    <CAShell theme={theme} icon={item.icon} title={item.title} sub={item.sub} onClose={onClose}
      footer={<>
        <Btn variant="secondary" icon={<Icon.mail size={14} />} onClick={() => { onClose(); onMessage?.(); }}>{t('customers.actions.text10')}</Btn>
        <div style={{ display: "flex", gap: T.space[2] }}>
          {actions.map((a, i) => (a.primary ? (
            <Btn key={i} variant="primary" disabled={busy} onClick={a.run} style={{ background: theme.accent }}>{a.label}</Btn>
          ) : (
            <Btn key={i} variant="secondary" danger={a.danger} disabled={busy} onClick={a.run}>{a.label}</Btn>
          )))}
        </div>
      </>}>
      {item.status ? (
        <div style={{ marginBottom: T.space[4] }}>
          {/* `status.fg`/`status.bg` arrive on the CAItemModel contract as ALREADY
              token-resolved values (T.green1 / alpha(T.green1,12) …) chosen by the
              tenant vocabulary in customers-config — i.e. per-vocabulary data, not
              literals. So the DS `Pill` shell carries them instead of a forked span. */}
          <Pill style={{ background: item.status.bg, color: item.status.fg }}>{item.status.label}</Pill>
        </div>
      ) : null}
      {rows.length ? (
        <SummaryList>
          {rows.map((r, i) => <SummaryRow key={i} label={r.k} value={r.v} />)}
        </SummaryList>
      ) : (
        <InlineEmptyState size="sm" title={t('customers.actions.noDetails', 'No details recorded for this item.')} />
      )}
      {item.note ? <Banner tone="primary" size="compact" style={{ marginTop: T.space[4] }}>{item.note}</Banner> : null}
    </CAShell>
  );
}
